import React, { useState, useEffect, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import {
  Star, ChevronUp, ChevronDown, Trash2, Search, X,
  Loader2, CheckCircle2, ArrowLeftRight, CalendarDays, Shuffle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDateRange(monday, sunday) {
  const fmt = d => new Date(d + "T12:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  return `${fmt(monday)} – ${fmt(sunday)}`;
}

// ── Artist-Suche Dropdown ─────────────────────────────────────────────────────
function ArtistPicker({ token, onSelect, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/api/artists/?search=${encodeURIComponent(query)}&limit=10`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setResults(data.artists || data.items || data || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [query, token]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md mx-4 overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="artist-picker-modal"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800">
          <Search size={16} className="text-zinc-400 flex-shrink-0" />
          <input
            autoFocus
            type="text"
            placeholder="Artist suchen…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="flex-1 bg-transparent text-white text-sm placeholder-zinc-500 focus:outline-none"
            data-testid="artist-search-input"
          />
          {loading && <Loader2 size={14} className="animate-spin text-zinc-500" />}
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {results.length === 0 && query.trim() && !loading && (
            <p className="text-zinc-500 text-sm text-center py-8">Keine Artists gefunden</p>
          )}
          {!query.trim() && (
            <p className="text-zinc-500 text-sm text-center py-8">Artist-Name eingeben…</p>
          )}
          {results.map(a => (
            <button
              key={a.id || a._id}
              onClick={() => onSelect(a)}
              data-testid={`artist-option-${a.id || a._id}`}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-left"
            >
              <div className="w-9 h-9 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                {a.bild_url
                  ? <img src={a.bild_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-zinc-500 font-bold text-sm">{a.name?.[0]}</div>
                }
              </div>
              <span className="text-white text-sm font-medium truncate">{a.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Einzelne Wochen-Zeile ─────────────────────────────────────────────────────
function WeekRow({ week, index, total, onAssign, onRemove, onMoveUp, onMoveDown, saving }) {
  const isCurrent = week.is_current;
  const isPast    = week.is_past;
  const hasArtist = !!week.artist;

  return (
    <div
      data-testid={`week-row-${week.week_year}-${week.week_num}`}
      className={`flex items-center gap-3 px-4 py-3 transition-colors rounded-xl ${
        isCurrent
          ? "bg-rose-600/10 border border-rose-500/30"
          : isPast
          ? "opacity-45"
          : "hover:bg-zinc-800/40"
      }`}
    >
      {/* KW Badge */}
      <div className="flex-shrink-0 w-14 text-center">
        <span className={`text-xs font-bold rounded-md px-1.5 py-0.5 ${
          isCurrent ? "bg-rose-600/20 text-rose-400" : "bg-zinc-800 text-zinc-500"
        }`}>
          KW {week.week_num}
        </span>
        {isCurrent && <p className="text-rose-500 text-[10px] font-semibold mt-0.5">Aktuell</p>}
      </div>

      {/* Datum */}
      <div className="w-28 flex-shrink-0">
        <p className="text-zinc-400 text-xs">{formatDateRange(week.monday, week.sunday)}</p>
      </div>

      {/* Artist / Fallback */}
      <div className="flex-1 min-w-0">
        {hasArtist ? (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md overflow-hidden bg-zinc-800 flex-shrink-0">
              {week.artist.bild_url
                ? <img src={week.artist.bild_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs font-bold">{week.artist.name?.[0]}</div>
              }
            </div>
            <span className="text-white text-sm font-semibold truncate">{week.artist.name}</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-zinc-500">
            <Shuffle size={13} />
            <span className="text-xs">Zufällig (Fallback)</span>
          </div>
        )}
      </div>

      {/* Aktionen */}
      {!isPast && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Move Up / Down */}
          <button
            onClick={() => onMoveUp(index)}
            disabled={!hasArtist || index <= 2 || saving}
            title="Eine Woche früher"
            className="p-1.5 text-zinc-500 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors rounded-md hover:bg-zinc-700"
            data-testid={`move-up-${week.week_num}`}
          >
            <ChevronUp size={14} />
          </button>
          <button
            onClick={() => onMoveDown(index)}
            disabled={!hasArtist || index >= total - 1 || saving}
            title="Eine Woche später"
            className="p-1.5 text-zinc-500 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors rounded-md hover:bg-zinc-700"
            data-testid={`move-down-${week.week_num}`}
          >
            <ChevronDown size={14} />
          </button>

          {/* Assign */}
          <button
            onClick={() => onAssign(week)}
            disabled={saving}
            title={hasArtist ? "Artist ändern" : "Artist zuweisen"}
            className="px-2.5 py-1.5 text-xs font-medium text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors disabled:opacity-40"
            data-testid={`assign-btn-${week.week_num}`}
          >
            {hasArtist ? "Ändern" : "Zuweisen"}
          </button>

          {/* Remove */}
          {hasArtist && (
            <button
              onClick={() => onRemove(week)}
              disabled={saving}
              title="Zuweisung entfernen"
              className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-40"
              data-testid={`remove-btn-${week.week_num}`}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Hauptseite ────────────────────────────────────────────────────────────────
export default function SpotlightAdminPage() {
  const { token } = useAuth();
  const [weeks, setWeeks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // week object
  const [toast, setToast]         = useState(null);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/artists/admin/spotlight/schedule?weeks_ahead=14`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setWeeks(await res.json());
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const handleAssign = async (week, artist) => {
    setPickerFor(null);
    setSaving(true);
    try {
      const res = await fetch(
        `${API}/api/artists/admin/spotlight/schedule/${week.week_year}/${week.week_num}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ artist_id: artist.id || artist._id }),
        }
      );
      if (res.ok) {
        showToast(`${artist.name} für KW ${week.week_num} gesetzt`);
        await fetchSchedule();
      } else {
        const d = await res.json();
        showToast(d.detail || "Fehler", false);
      }
    } catch (e) { showToast(e.message, false); }
    setSaving(false);
  };

  const handleRemove = async (week) => {
    setSaving(true);
    try {
      await fetch(
        `${API}/api/artists/admin/spotlight/schedule/${week.week_year}/${week.week_num}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(`KW ${week.week_num} zurückgesetzt`);
      await fetchSchedule();
    } catch (e) { showToast(e.message, false); }
    setSaving(false);
  };

  const handleSwap = async (indexA, indexB) => {
    const a = weeks[indexA];
    const b = weeks[indexB];
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/artists/admin/spotlight/schedule/swap`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          a: { year: a.week_year, week: a.week_num },
          b: { year: b.week_year, week: b.week_num },
        }),
      });
      if (res.ok) { showToast("Wochen getauscht"); await fetchSchedule(); }
    } catch (e) { showToast(e.message, false); }
    setSaving(false);
  };

  const plannedCount = weeks.filter(w => w.artist && !w.is_past).length;

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-black text-white font-heading">Artist Spotlight Planung</h1>
            <p className="text-zinc-500 text-sm mt-0.5">
              Manuell geplante Artists haben Vorrang · ohne Planung greift der Zufalls-Fallback
            </p>
          </div>
          {saving && <Loader2 size={18} className="animate-spin text-zinc-400 flex-shrink-0 mt-1" />}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <StatCard icon={<CalendarDays size={14} />} label="Nächste Wochen" value={weeks.filter(w => !w.is_past).length} />
          <StatCard icon={<Star size={14} />} label="Geplant" value={plannedCount} highlight />
          <StatCard icon={<Shuffle size={14} />} label="Zufalls-Fallback" value={weeks.filter(w => !w.is_past && !w.artist).length} />
        </div>

        {/* Legende */}
        <div className="flex items-center gap-4 text-xs text-zinc-500 mb-6">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-rose-500/40 border border-rose-500/50 inline-block" /> Aktuelle Woche</span>
          <span className="flex items-center gap-1.5"><ArrowLeftRight size={11} /> Pfeile = Artist eine Woche verschieben</span>
        </div>

        {/* Tabelle */}
        <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Wochen-Übersicht</h2>
            <span className="text-xs text-zinc-500">2 vergangene · aktuell · 14 bevorstehende</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-zinc-500">
              <Loader2 size={20} className="animate-spin mr-2" /> Lade Plan…
            </div>
          ) : (
            <div className="p-2 space-y-0.5">
              {weeks.map((week, idx) => (
                <WeekRow
                  key={`${week.week_year}-${week.week_num}`}
                  week={week}
                  index={idx}
                  total={weeks.length}
                  saving={saving}
                  onAssign={w => setPickerFor(w)}
                  onRemove={handleRemove}
                  onMoveUp={i => handleSwap(i, i - 1)}
                  onMoveDown={i => handleSwap(i, i + 1)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Artist Picker Modal */}
      {pickerFor && (
        <ArtistPicker
          token={token}
          onClose={() => setPickerFor(null)}
          onSelect={artist => handleAssign(pickerFor, artist)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 px-4 py-2.5 rounded-xl shadow-xl text-sm font-medium transition-all ${
          toast.ok ? "bg-emerald-600 text-white" : "bg-red-600 text-white"
        }`} data-testid="spotlight-toast">
          {toast.ok ? <CheckCircle2 size={15} /> : <X size={15} />}
          {toast.msg}
        </div>
      )}
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-3 ${highlight ? "border-rose-500/20 bg-rose-500/5" : "border-zinc-800/50 bg-zinc-900/60"}`}>
      <div className={`flex items-center gap-1.5 text-xs mb-1.5 ${highlight ? "text-rose-400" : "text-zinc-500"}`}>
        {icon} {label}
      </div>
      <p className={`text-xl font-black ${highlight ? "text-rose-400" : "text-white"}`}>{value}</p>
    </div>
  );
}
